import React, { useMemo, useState } from 'react';

const SortIcon = ({ direction }) => (
  <svg className="w-3 h-3 inline-block ml-1" viewBox="0 0 20 20" fill="currentColor">
    {direction === 'asc' ? (
      <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
    ) : (
      <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
    )}
  </svg>
);

function formatSignalTime(unixMs) {
  if (!unixMs) return null;
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York'
  });
}

function BreakoutTable({ title, signals, tickers }) {
  const [sortStack, setSortStack] = useState([]);

  const rows = useMemo(() => {
    const cmpCol = (a, b, col, dir) => {
      let aVal, bVal;
      if (col === 'time') {
        aVal = a.signal_time || 0;
        bVal = b.signal_time || 0;
      } else {
        aVal = a.changePercent != null ? a.changePercent : (dir === 'asc' ? 999 : -999);
        bVal = b.changePercent != null ? b.changePercent : (dir === 'asc' ? 999 : -999);
      }
      return dir === 'asc' ? aVal - bVal : bVal - aVal;
    };

    return signals
      .map((sig) => {
        const live = tickers[sig.symbol];
        const price = live ? live.price : sig.price || null;
        let changePercent = null;
        const refOpen = sig.ref_open || 0;
        if (price && refOpen > 0) {
          changePercent = ((price - refOpen) / refOpen) * 100;
        }
        return { ...sig, livePrice: price, changePercent };
      })
      .sort((a, b) => {
        for (const { col, dir } of sortStack) {
          const cmp = cmpCol(a, b, col, dir);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
  }, [signals, tickers, sortStack]);

  const handleSort = (col) => {
    setSortStack((prev) => {
      const idx = prev.findIndex((s) => s.col === col);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = { col, dir: updated[idx].dir === 'asc' ? 'desc' : 'asc' };
        return updated;
      }
      return [...prev, { col, dir: 'asc' }];
    });
  };

  const clearSort = () => setSortStack([]);
  const sortIndex = (col) => sortStack.findIndex((s) => s.col === col);
  const sortDirFor = (col) => { const e = sortStack.find((s) => s.col === col); return e ? e.dir : null; };

  const openTradingView = (symbol) => {
    window.open(
      `https://www.tradingview.com/chart/?symbol=${symbol}&interval=1`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wider">{title}</h2>
          <div className="flex items-center gap-3">
            {sortStack.length > 1 && (
              <button onClick={clearSort} className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors uppercase tracking-wider">Clear sort</button>
            )}
            <span className="text-xs text-gray-500">
              {rows.length} {rows.length === 1 ? 'ticker' : 'tickers'}
            </span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-900 z-10">
            <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 sm:px-6 py-3">Ticker</th>
              <th className="px-4 sm:px-6 py-3 text-right">Price</th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortIndex('change') !== -1 ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('change')}
              >
                Change %
                {sortDirFor('change') && <SortIcon direction={sortDirFor('change')} />}
                {sortStack.length > 1 && sortIndex('change') !== -1 && <span className="ml-1 text-[9px] text-gray-500">{sortIndex('change') + 1}</span>}
              </th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortIndex('time') !== -1 ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('time')}
              >
                Time
                {sortDirFor('time') && <SortIcon direction={sortDirFor('time')} />}
                {sortStack.length > 1 && sortIndex('time') !== -1 && <span className="ml-1 text-[9px] text-gray-500">{sortIndex('time') + 1}</span>}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 sm:px-6 py-12 text-center">
                  <span className="text-sm text-gray-500">No signals yet</span>
                </td>
              </tr>
            ) : rows.map((row) => {
                const hasPrice = row.livePrice != null;
                const isPositive = row.changePercent >= 0;
                const timeStr = formatSignalTime(row.signal_time);
                return (
                  <tr
                    key={row.symbol}
                    onClick={() => openTradingView(row.symbol)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-sm font-semibold text-gray-200">{row.symbol}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {hasPrice ? (
                        <span className="text-sm text-gray-300 font-medium tabular-nums">
                          ${row.livePrice.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {row.changePercent != null ? (
                        <span
                          className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${
                            isPositive ? 'text-green-400' : 'text-red-400'
                          }`}
                        >
                          {isPositive ? (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {isPositive ? '+' : ''}
                          {row.changePercent.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {timeStr ? (
                        <span className="text-sm text-gray-400 tabular-nums">{timeStr}</span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ConvergenceTable({ signals, tickers }) {
  // sortStack: array of { col, dir } — first entry = highest priority
  const [sortStack, setSortStack] = useState([]);

  const rows = useMemo(() => {
    const cmpCol = (a, b, col, dir) => {
      let aVal, bVal;
      if (col === 'time') {
        aVal = a.signal_time || 0;
        bVal = b.signal_time || 0;
      } else if (col === 'gap') {
        aVal = a.gapPercent != null ? a.gapPercent : 999;
        bVal = b.gapPercent != null ? b.gapPercent : 999;
      } else {
        aVal = a.changePercent != null ? a.changePercent : (dir === 'asc' ? 999 : -999);
        bVal = b.changePercent != null ? b.changePercent : (dir === 'asc' ? 999 : -999);
      }
      return dir === 'asc' ? aVal - bVal : bVal - aVal;
    };

    return (signals || [])
      .map((sig) => {
        const live = tickers[sig.symbol];
        const price = live ? live.price : sig.price || null;
        const gapPercent = sig.gap_percent != null ? sig.gap_percent : sig.ema_value || null;
        let changePercent = sig.change_percent || null;
        if (price && sig.ref_open > 0 && changePercent == null) {
          changePercent = ((price - sig.ref_open) / sig.ref_open) * 100;
        }
        if (live && live.changePercent != null) {
          changePercent = live.changePercent;
        }
        return { ...sig, livePrice: price, gapPercent, changePercent };
      })
      .sort((a, b) => {
        for (const { col, dir } of sortStack) {
          const cmp = cmpCol(a, b, col, dir);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
  }, [signals, tickers, sortStack]);

  const handleSort = (col) => {
    setSortStack((prev) => {
      const idx = prev.findIndex((s) => s.col === col);
      if (idx !== -1) {
        // Already in stack — toggle its direction
        const updated = [...prev];
        updated[idx] = { col, dir: updated[idx].dir === 'asc' ? 'desc' : 'asc' };
        return updated;
      }
      // Add to end of stack (lowest priority)
      return [...prev, { col, dir: 'asc' }];
    });
  };

  const clearSort = () => setSortStack([]);

  const sortIndex = (col) => {
    const idx = sortStack.findIndex((s) => s.col === col);
    return idx !== -1 ? idx : -1;
  };

  const sortDirFor = (col) => {
    const entry = sortStack.find((s) => s.col === col);
    return entry ? entry.dir : null;
  };

  const openTradingView = (symbol) => {
    window.open(
      `https://www.tradingview.com/chart/?symbol=${symbol}&interval=5`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden mb-6">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wider">200 EMA &gt; 100 EMA</h2>
          <div className="flex items-center gap-3">
            {sortStack.length > 1 && (
              <button
                onClick={clearSort}
                className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors uppercase tracking-wider"
              >
                Clear sort
              </button>
            )}
            <span className="text-xs text-gray-500">
              {rows.length} {rows.length === 1 ? 'ticker' : 'tickers'}
            </span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-900 z-10">
            <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 sm:px-6 py-3">Ticker</th>
              <th className="px-4 sm:px-6 py-3 text-right">Price</th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortIndex('gap') !== -1 ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('gap')}
              >
                Gap %
                {sortDirFor('gap') && <SortIcon direction={sortDirFor('gap')} />}
                {sortStack.length > 1 && sortIndex('gap') !== -1 && (
                  <span className="ml-1 text-[9px] text-gray-500">{sortIndex('gap') + 1}</span>
                )}
              </th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortIndex('change') !== -1 ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('change')}
              >
                Change %
                {sortDirFor('change') && <SortIcon direction={sortDirFor('change')} />}
                {sortStack.length > 1 && sortIndex('change') !== -1 && (
                  <span className="ml-1 text-[9px] text-gray-500">{sortIndex('change') + 1}</span>
                )}
              </th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortIndex('time') !== -1 ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('time')}
              >
                Time
                {sortDirFor('time') && <SortIcon direction={sortDirFor('time')} />}
                {sortStack.length > 1 && sortIndex('time') !== -1 && (
                  <span className="ml-1 text-[9px] text-gray-500">{sortIndex('time') + 1}</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 sm:px-6 py-12 text-center">
                  <span className="text-sm text-gray-500">No signals yet</span>
                </td>
              </tr>
            ) : rows.map((row) => {
                const hasPrice = row.livePrice != null;
                const isPositive = row.changePercent != null && row.changePercent >= 0;
                const timeStr = formatSignalTime(row.signal_time);
                return (
                  <tr
                    key={row.symbol}
                    onClick={() => openTradingView(row.symbol)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-sm font-semibold text-gray-200">{row.symbol}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {hasPrice ? (
                        <span className="text-sm text-gray-300 font-medium tabular-nums">
                          ${row.livePrice.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {row.gapPercent != null ? (
                        <span className="text-sm font-semibold tabular-nums text-yellow-400">
                          {row.gapPercent.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {row.changePercent != null ? (
                        <span
                          className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${
                            isPositive ? 'text-green-400' : 'text-red-400'
                          }`}
                        >
                          {isPositive ? (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {isPositive ? '+' : ''}
                          {row.changePercent.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {timeStr ? (
                        <span className="text-sm text-gray-400 tabular-nums">{timeStr}</span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BreakoutTables({ longs, shorts, tickers }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <BreakoutTable title="Long (Above 9 EMA)" signals={longs} tickers={tickers} />
      <BreakoutTable title="Short (Below 9 EMA)" signals={shorts} tickers={tickers} />
    </div>
  );
}
